import { computed } from "vue";

// Shared editing model for the admin pricing tables (global/per-user pricing
// and a partner's table). Rows are held as {from, to, price} while editing and
// flattened back to the API's {from, price} bands on submit.
//
// Hosts must expose a `schema` ref and a reactive `errors` object. Row errors
// are keyed by row index, so a host that mixes pricing rows with form-field
// errors has to tell the two apart by key.
const isBlankValue = (value) =>
  value === null || value === undefined || value === "";

const pricingSchemaMixin = {
  computed: {
    isLastRowEmpty() {
      if (!this.schema.length) {
        return false;
      }
      return this.isBlankPricingRow(this.schema[this.schema.length - 1]);
    },
  },
  methods: {
    // Blankness is about a field being empty, never about its value being
    // falsy: a price of 0 is a filled-in row that fails validation, not an
    // unfilled one.
    isBlankPricingRow(row) {
      return isBlankValue(row.price) && isBlankValue(row.to);
    },
    // The table always carries a trailing placeholder row for the next band;
    // it is not a band until it is filled in.
    pricingPayload() {
      return this.schema
        .filter((row) => !this.isBlankPricingRow(row))
        .map((row) => ({ from: row["from"] - 1, price: row["price"] }));
    },
    setSchema(pricingSchema) {
      this.schema = pricingSchema.reduce(
        (previousValue, currentValue, index, array) => {
          const nextIndex = index + 1;
          previousValue.push({
            from: currentValue["from"] + 1,
            to: array[nextIndex] ? array[nextIndex]["from"] : null,
            price: currentValue["price"],
            id: index,
          });
          return previousValue;
        },
        []
      );
      this.schema.forEach((row, rowIndex) => {
        const previousLine = this.schema[rowIndex - 1];
        row.from = previousLine ? computed(() => previousLine?.to + 1) : 1;
      });
      if (this.schema.length === 0) {
        this.addNewLine();
      } else {
        this.validateAll();
      }
    },
    onRowInput(rowIndex) {
      this.validatePricingRowsFrom(rowIndex);
    },
    // Editing a row can invalidate the ones after it (a band's `from` follows
    // the previous band's `to`), and validating the edited row may append a
    // placeholder, so walk back down from the last row to the edited one.
    validatePricingRowsFrom(rowIndex) {
      this.$nextTick(() => {
        let index = this.schema.length - 1;
        while (index >= rowIndex) {
          if (this.schema[index]) {
            this.validateRow(this.schema[index], index);
          }
          index -= 1;
        }
      });
    },
    validateAll() {
      this.schema.forEach((row, rowIndex) => {
        this.validateRow(row, rowIndex);
      });
    },
    validateRow(row, rowIndex) {
      if (!row) {
        return;
      }
      const isLastRow = rowIndex === this.schema.length - 1;

      if (isLastRow && this.isLastRowEmpty) {
        const previousRow = this.schema[rowIndex - 1];
        if (previousRow && isBlankValue(previousRow.to)) {
          this.deleteRow(row.id);
          return;
        }
      }

      const errors = this.getRowErrors(row, rowIndex);
      const hasErrors = Object.values(errors).reduce(
        (prevValue, currentValue) => {
          return prevValue || currentValue;
        },
        false
      );
      if (hasErrors) {
        Object.assign(this.errors, this.errors, {
          [rowIndex]: errors,
        });
      } else {
        delete this.errors[rowIndex];
      }
      const isLastLine = rowIndex === this.schema.length - 1;
      if (isLastLine && !hasErrors && row.to) {
        this.addNewLine();
      }
    },
    deleteRow(rowId) {
      let index = 0;
      for (let row of this.schema) {
        if (row.id === rowId) {
          this.schema.splice(index, 1);
          delete this.errors[index];
          break;
        }
        index += 1;
      }
      this.validateAll();
    },
    getRowErrors(row, rowIndex) {
      const isLastRow = rowIndex === this.schema.length - 1;
      const isPreLastRow = rowIndex === this.schema.length - 2;
      const isEmpty = this.isBlankPricingRow(row);
      // The final band is open-ended, so it alone may leave `to` empty.
      const isLastNonEmptyRow =
        ((this.isLastRowEmpty && isPreLastRow) || isLastRow) && !isEmpty;

      const errors = {};
      if (isBlankValue(row.price) || !(Number(row.price) > 0)) {
        errors["price"] = true;
      }
      if (isBlankValue(row.to)) {
        if (!isLastNonEmptyRow) {
          errors["to"] = true;
        }
      } else if (row.to < row.from) {
        errors["to"] = true;
      }
      return errors;
    },
    addNewLine() {
      const index = this.schema.length;
      const previousLine = this.schema[index - 1];
      this.schema.push({
        from: previousLine ? computed(() => previousLine?.to + 1) : 1,
        to: null,
        price: null,
        id: index,
      });
      this.validateRow(this.schema[index], index);
    },
  },
};

export default pricingSchemaMixin;
